/**
 * One stylesheet, injected once, every selector prefixed `os-chart`.
 *
 * A `<style>` element applies to the whole document no matter where it is
 * appended, so a bare `table{…}` here would restyle every app in the OS. Every
 * structural property (display, flex-direction, width) is stated explicitly —
 * relying on CSS initial values is how panels collapse into one unreadable row.
 */

let injected = false;

export function ensureChartCss() {
  if (typeof document === "undefined" || injected) return;
  if (document.getElementById("arclog-charts-css")) {
    injected = true;
    return;
  }
  const s = document.createElement("style");
  s.id = "arclog-charts-css";
  s.textContent = `
/* An author \`display\` beats the UA sheet's \`[hidden]{display:none}\`, so every
   element here that sets display would ignore .hidden — the idle tooltip in
   particular rendered as an empty pill in the corner of every chart. */
.os-chart [hidden],.os-chart[hidden],.os-stat [hidden],.os-stat[hidden]{display:none!important}
.os-chart{display:flex;flex-direction:column;width:100%;min-width:0;min-height:0;height:100%;
  font-family:var(--os-font-ui,system-ui);color:var(--os-text);position:relative;isolation:isolate}
.os-chart-head{display:flex;flex-wrap:wrap;align-items:baseline;gap:6px 12px;
  padding:2px 2px 8px;min-width:0;flex:0 0 auto}
.os-chart-titles{display:flex;flex-direction:column;gap:1px;min-width:0;margin-right:auto}
.os-chart-title{font-size:13px;font-weight:620;letter-spacing:-.01em;color:var(--os-text);
  overflow:hidden;text-overflow:ellipsis}
.os-chart-sub{font-size:11px;color:var(--os-text-dim);overflow:hidden;text-overflow:ellipsis}
.os-chart-legend{display:flex;flex-wrap:wrap;align-items:center;gap:4px 12px;min-width:0}
.os-chart-legenditem{display:inline-flex;align-items:center;gap:6px;min-width:0;
  font-size:11px;font-weight:500;color:var(--os-text-dim);letter-spacing:-.01em}
.os-chart-legenditem.is-off{opacity:.4;text-decoration:line-through}
.os-chart-swatch{display:inline-block;width:9px;height:9px;border-radius:3px;flex:0 0 auto;
  box-shadow:0 0 0 1px rgba(0,0,0,.18) inset}
.os-chart-tablebtn{flex:0 0 auto;border:1px solid var(--os-glass-border,rgba(255,255,255,.14));
  background:transparent;color:var(--os-text-dim);border-radius:7px;padding:3px 9px;
  font:500 11px var(--os-font-ui,system-ui);cursor:pointer}
.os-chart-tablebtn:hover{background:rgba(127,127,127,.14);color:var(--os-text)}
.os-chart-tablebtn.is-on{background:var(--os-accent,#0a84ff);border-color:transparent;color:#fff}
.os-chart-body{position:relative;flex:1 1 auto;min-height:80px;min-width:0;width:100%}
.os-chart-canvas{position:absolute;inset:0;width:100%;height:100%}
.os-chart-layer{position:absolute;top:0;left:0;display:block}
.os-chart--spark .os-chart-body{min-height:24px}
.os-chart-tip{position:absolute;top:0;left:0;z-index:3;pointer-events:none;
  display:flex;flex-direction:column;gap:3px;min-width:0;max-width:240px;
  padding:7px 9px;border-radius:9px;
  background:var(--os-chart-tip-bg,rgba(24,26,34,.96));
  border:1px solid var(--os-glass-border,rgba(255,255,255,.14));
  box-shadow:0 10px 28px rgba(0,0,0,.35);
  font-size:11px;line-height:1.35;color:var(--os-text)}
.os-chart-tiphead{font-weight:620;color:var(--os-text);margin-bottom:1px;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.os-chart-tiprow{display:flex;align-items:center;gap:7px;min-width:0}
.os-chart-tipname{flex:1 1 auto;min-width:0;color:var(--os-text-dim);
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.os-chart-tipval{flex:0 0 auto;font-variant-numeric:tabular-nums;font-weight:600;color:var(--os-text)}
/* A px cap, not 40%: against a root of indefinite height a percentage resolves
   to none, and against a fixed one it clipped the table mid-row. */
.os-chart-tablewrap{flex:0 0 auto;max-height:260px;min-height:0;overflow:auto;margin-top:8px}
.os-chart-tablescroll{overflow-x:auto;width:100%;min-width:0}
table.os-chart-table{border-collapse:collapse;width:100%;font-size:11px;
  font-variant-numeric:tabular-nums;color:var(--os-text)}
table.os-chart-table caption{text-align:left;padding:0 0 6px;font-weight:600;color:var(--os-text-dim)}
table.os-chart-table th,table.os-chart-table td{padding:4px 10px 4px 0;text-align:right;
  border-bottom:1px solid var(--os-glass-border,rgba(255,255,255,.1));white-space:nowrap}
table.os-chart-table thead th{color:var(--os-text-dim);font-weight:600}
table.os-chart-table th[scope=row]{text-align:left;font-weight:500;color:var(--os-text-dim)}

.os-stat{display:flex;flex-direction:column;gap:2px;min-width:0;width:100%;
  font-family:var(--os-font-ui,system-ui);color:var(--os-text)}
.os-stat-label{font-size:11px;font-weight:500;color:var(--os-text-dim);
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.os-stat-row{display:flex;align-items:baseline;gap:8px;min-width:0;flex-wrap:wrap}
.os-stat-value{font-size:26px;font-weight:650;letter-spacing:-.02em;
  font-variant-numeric:tabular-nums;color:var(--os-text)}
.os-stat-delta{display:inline-flex;align-items:baseline;gap:3px;font-size:11.5px;font-weight:600;
  font-variant-numeric:tabular-nums}
.os-stat-deltaglyph{font-size:10px;line-height:1}
.os-stat-spark{width:100%;min-width:0;height:34px;margin-top:4px}
`;
  document.head.appendChild(s);
  injected = true;
}
